import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { store } from '#app/store'
import { Icon } from '#components/Icon'
import type { InfoGroup } from '#components/tabularOverlay/InfoEntry'
import { TabularInformationOverlay } from '#components/tabularOverlay/TabularInformationOverlay'
import { isTg3ApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { useFullApiConfigurationInfo } from '#features/Configuration/useFullApiConfigurationInfo'
import InfoIcon from '#icons/info-circle-outline-24.svg'
import type { TruckConfiguration } from '#store/slices/configurationSlice'
import { isValidDateString } from '#utils/DateString'
import { formatDateTime } from '#utils/formatDateTime'
import { formatDuration } from '#utils/formatDuration'
import { testIds } from '#utils/testIds'
import { useDenominationDisplayData } from '../components/useDenominationDisplayData'

const useConfigurationInformation = (config: TruckConfiguration, isLive: boolean): InfoGroup[] => {
  const { t, i18n } = useTranslation()

  const [remainingSessionTime, setRemainingSessionTime] = useState<number | undefined>(undefined)
  useEffect(() => {
    const handler = () => {
      const sessionExpiry = store.getState().truckConfigurations.meta?.sessionExpiryInMs
      if (config.uuid && config.lastApiInteraction && sessionExpiry) {
        setRemainingSessionTime(config.lastApiInteraction + sessionExpiry - Date.now())
      } else {
        setRemainingSessionTime(undefined)
      }
    }

    handler()

    if (!isLive) {
      return
    }

    const interval = setInterval(handler, 1000)
    return () => clearInterval(interval)
  }, [config.lastApiInteraction, isLive, config.uuid])

  const { marketDenomination, configurationTitle } = useDenominationDisplayData(config)

  const configurationInfo: InfoGroup = {
    groupId: 'configuration',
    title: t('configuration.configuration-information.groups.configuration.title', 'Configuration'),
    entries: [
      {
        entryId: 'configuration-id',
        title: t('configuration.configuration-information.groups.configuration.entries.configuration-id', 'Configuration ID'),
        value: config.uuid ?? undefined,
      },
      {
        entryId: 'title',
        title: t('configuration.configuration-information.groups.configuration.entries.title', 'Title'),
        value: configurationTitle,
      },
      {
        entryId: 'market-denomination',
        title: t('configuration.configuration-information.groups.configuration.entries.market-denomination', 'Market Denomination'),
        value: marketDenomination,
      },
      {
        entryId: 'last-changed',
        title: t('configuration.configuration-information.groups.configuration.entries.last-changed', 'Last Changed'),
        value: config.lastChange?.when ? formatDateTime(i18n, config.lastChange.when) : undefined,
      },
      {
        entryId: 'remaining-session-time',
        title: t('configuration.configuration-information.groups.configuration.entries.remaining-session-time', 'Remaining Session Time'),
        value: remainingSessionTime ? formatDuration(remainingSessionTime, t, { relative: false }) : undefined,
      },
      {
        entryId: 'version',
        title: t('configuration.configuration-information.groups.configuration.entries.version', 'Version'),
        value: `v${config.version}`,
      },
    ],
  }

  if (isTg3ApiConfiguration(config.callingApiConfiguration)) {
    const tg3CodeInfo: InfoGroup = {
      groupId: 'tg3-code',
      title: t('configuration.configuration-information.groups.tg3-code.title', 'TG3-Code'),
      entries: [
        {
          entryId: 'code',
          title: t('configuration.configuration-information.groups.tg3-code.entries.code', 'Code'),
          value: config.callingApiConfiguration.tg3Code,
        },
        {
          entryId: 'orderCountry',
          title: t('configuration.configuration-information.groups.tg3-code.entries.orderCountry', 'Order Country'),
          value: config.apiConfiguration.orderCountry,
        },
        {
          entryId: 'language',
          title: t('configuration.configuration-information.groups.tg3-code.entries.language', 'Language'),
          value: config.apiConfiguration.orderLanguage,
        },
        {
          entryId: 'desiredDealerDate',
          title: t('configuration.configuration-information.groups.tg3-code.entries.desiredDealerDate', 'Desired Dealer Date'),
          value: isValidDateString(config.apiConfiguration.desiredDealerDate) ? config.apiConfiguration.desiredDealerDate : undefined,
        },
        {
          entryId: 'last-saved',
          title: t('configuration.configuration-information.groups.tg3-code.entries.last-saved', 'Last Saved'),
          value: config.lastManCodeUpdate?.when ? formatDateTime(i18n, config.lastManCodeUpdate.when) : undefined,
        },
      ],
    }

    return [tg3CodeInfo, configurationInfo]
  }

  const apiConfigurationInfo = useFullApiConfigurationInfo(config.apiConfiguration)

  const manCodeInfo: InfoGroup = {
    groupId: 'man-code',
    title: t('configuration.configuration-information.groups.man-code.title', 'MAN-Code'),
    entries: [
      {
        entryId: 'code',
        title: t('configuration.configuration-information.groups.man-code.entries.code', 'Code'),
        value: config.apiConfiguration.manCode,
      },
      {
        entryId: 'last-saved',
        title: t('configuration.configuration-information.groups.man-code.entries.last-saved', 'Last Saved'),
        value: config.lastManCodeUpdate?.when ? formatDateTime(i18n, config.lastManCodeUpdate.when) : undefined,
      },
      {
        entryId: 'version',
        title: t('configuration.configuration-information.groups.man-code.entries.version', 'Version'),
        value: config.lastManCodeUpdate?.version ? `v${config.lastManCodeUpdate.version}` : undefined,
      },
    ],
  }

  return [manCodeInfo, configurationInfo, apiConfigurationInfo]
}

export const ConfigurationInformationOverlay: FC<{
  config: TruckConfiguration
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}> = ({ config, isOpen, setIsOpen }) => {
  const { t } = useTranslation()
  const infoGroups = useConfigurationInformation(config, isOpen)

  if (!isOpen) {
    return null
  }

  return (
    <TabularInformationOverlay
      title={t('configuration.configuration-information.title', 'Configuration Information')}
      infoGroups={infoGroups}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  )
}

export const ConfigurationInformationOverlayElements = ({ config }: { config: TruckConfiguration }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  return (
    <>
      <div
        className="_entry"
        onClick={() => {
          setIsOpen(true)
        }}
        data-testid={testIds.header.dropdownEntry.configurationInformation}
        data-disabled="false"
      >
        <Icon iconUrl={InfoIcon} />
        {t('configuration.actions.button.configuration-information', 'Show configuration information')}
      </div>

      {isOpen && <ConfigurationInformationOverlay isOpen={isOpen} setIsOpen={setIsOpen} config={config} />}
    </>
  )
}
